import { Loader2Icon, Moon, Sun } from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import { Label } from '@/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shadcn/select';
import { Skeleton } from '@/shadcn/skeleton';
import { useTheme } from '@/shadcn/theme/theme-provider';

import { PageUnauthorized } from '@/components/pageUnauthorized/pageUnauthorized';
import { sandboxMapping } from '@/components/sandbox/sandboxMapping';
import * as routes from '@/constant/routes';
import { useUser } from '@/hooks/useUser';

/**
 * Sandbox page for development and testing
 * Use the Select to choose the component to test, and an other Select to choose the variant of the component
 * Then the component will be rendered in the center of the page
 */
export const SandboxPage = () => {
  const history = useHistory();
  const { isPrivileged, loading } = useUser();
  const { setTheme, theme } = useTheme();

  const { component, variant } = useParams<{
    component?: string;
    variant?: string;
  }>();

  const onChangeTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  if (loading) {
    return (
      <Skeleton className="flex h-screen w-screen items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-primary" />
      </Skeleton>
    );
  }

  if (!isPrivileged) {
    return <PageUnauthorized />;
  }

  const sandboxComponents = Object.keys(sandboxMapping);

  if (sandboxComponents.length === 0) {
    return (
      <section className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-4">
        <p>No sandbox components available.</p>
      </section>
    );
  }

  const fallbackComponent = sandboxComponents[0];
  const selectedComponent =
    component && sandboxMapping[component] ? component : fallbackComponent;
  const selectedEntry = sandboxMapping[selectedComponent];
  const variants = Object.keys(selectedEntry.variants);

  if (variants.length === 0) {
    return (
      <section className="flex size-full flex-col items-center justify-center gap-4 p-4">
        <p>No sandbox variants available for {selectedComponent}.</p>
      </section>
    );
  }

  const fallbackVariant =
    selectedEntry.defaultVariant &&
    selectedEntry.variants[selectedEntry.defaultVariant]
      ? selectedEntry.defaultVariant
      : variants[0];
  const selectedVariant =
    variant && selectedEntry.variants[variant] ? variant : fallbackVariant;
  const Component = selectedEntry.variants[selectedVariant];

  const pushSandboxPath = (nextComponent: string, nextVariant?: string) => {
    if (nextVariant) {
      history.push(
        `${routes.ROUTE_SANDBOX_BASE}/${encodeURIComponent(nextComponent)}/${encodeURIComponent(nextVariant)}`
      );
      return;
    }

    history.push(
      `${routes.ROUTE_SANDBOX_BASE}/${encodeURIComponent(nextComponent)}`
    );
  };

  const handleComponentChange = (nextComponent: string) => {
    const nextEntry = sandboxMapping[nextComponent];
    const componentVariants = Object.keys(nextEntry.variants);
    const nextVariant =
      nextEntry.defaultVariant && nextEntry.variants[nextEntry.defaultVariant]
        ? nextEntry.defaultVariant
        : componentVariants[0];

    pushSandboxPath(nextComponent, nextVariant);
  };
  const handleVariantChange = (nextVariant: string) => {
    pushSandboxPath(selectedComponent, nextVariant);
  };

  return (
    <section className="flex h-screen w-screen flex-col">
      <header className="flex flex-row items-center gap-6 border-border border-b bg-card px-8 py-4">
        <div className="w-[200px] space-y-2">
          <Label htmlFor="component-select">Select a component</Label>
          <Select
            onValueChange={handleComponentChange}
            value={selectedComponent}
          >
            <SelectTrigger id="component-select">
              <SelectValue placeholder="Select a component" />
            </SelectTrigger>
            <SelectContent>
              {sandboxComponents.map(component => (
                <SelectItem
                  className="first-letter:uppercase"
                  key={component}
                  value={component}
                >
                  {component}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[200px] space-y-2">
          <Label htmlFor="variant-select">Select a variant</Label>
          <Select onValueChange={handleVariantChange} value={selectedVariant}>
            <SelectTrigger id="variant-select">
              <SelectValue placeholder="Select a variant" />
            </SelectTrigger>
            <SelectContent>
              {variants.map(componentVariant => (
                <SelectItem
                  className="first-letter:uppercase"
                  key={componentVariant}
                  value={componentVariant}
                >
                  {componentVariant}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className="ml-auto"
          onClick={onChangeTheme}
          size="icon"
          variant="ghost"
        >
          <Sun className="dark:-rotate-90 h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:scale-0" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </header>
      <section
        className="flex size-full items-center justify-center rounded-md border-border/10 border-dashed p-4"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in oklab, var(--color-border) 100%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklab, var(--color-border) 100%, transparent) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          backgroundPosition: 'center'
        }}
      >
        <Component />
      </section>
    </section>
  );
};
